import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { jsPDF } from "jspdf";
import PropTypes from "prop-types";

const DEFAULT_HEIGHT_PX = 180;

/**
 * Raster canvas → single-page PDF (e.g. backend expects application/pdf).
 * @param {HTMLCanvasElement | null} canvas
 * @param {{ fileName?: string, margin?: number, maxHeightRatio?: number, maxHeightCap?: number }} [options]
 * @returns {File | null}
 */
export function canvasToSignaturePdf(canvas, options = {}) {
  if (!canvas?.width || !canvas?.height) return null;
  const {
    fileName = "signature.pdf",
    margin = 48,
    maxHeightRatio = 0.35,
    maxHeightCap = 220
  } = options;

  const imgData = canvas.toDataURL("image/png", 1);
  const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const maxW = pageW - margin * 2;
  const maxH = Math.min(pageH * maxHeightRatio, maxHeightCap);
  const cw = canvas.width;
  const ch = canvas.height;
  const aspect = cw / ch;
  let w = maxW;
  let h = w / aspect;
  if (h > maxH) {
    h = maxH;
    w = h * aspect;
  }
  const x = (pageW - w) / 2;
  const y = margin;
  pdf.addImage(imgData, "PNG", x, y, w, h);
  const blob = pdf.output("blob");
  return new File([blob], fileName, { type: "application/pdf" });
}

/**
 * @typedef {object} SignaturePadRef
 * @property {() => void} clear
 * @property {() => HTMLCanvasElement | null} getCanvas
 * @property {() => boolean} hasSignature
 * @property {(options?: { fileName?: string } & Parameters<typeof canvasToSignaturePdf>[1]) => File | null} exportPdf
 */

const SignaturePad = forwardRef(function SignaturePad(
  {
    heightPx = DEFAULT_HEIGHT_PX,
    minWidthPx = 260,
    label,
    hint,
    clearLabel = "Clear",
    showError = false,
    errorMessage,
    onSignatureChange,
    className = "",
    ariaLabel = "Signature"
  },
  ref
) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });
  const [hasSignature, setHasSignature] = useState(false);
  // Parent often passes an inline callback; keep notifyChange stable so initCanvas /
  // useLayoutEffect do not re-run every render and wipe the canvas.
  const onSignatureChangeRef = useRef(onSignatureChange);
  useEffect(() => {
    onSignatureChangeRef.current = onSignatureChange;
  }, [onSignatureChange]);

  const notifyChange = useCallback((next) => {
    setHasSignature(next);
    onSignatureChangeRef.current?.(next);
  }, []);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const rawW = container.getBoundingClientRect().width;
    const w = Math.max(minWidthPx, Math.floor(rawW || 400));
    const h = heightPx;
    const dpr = window.devicePixelRatio || 1;

    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    canvas.style.touchAction = "none";

    const ctx = canvas.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = "#111111";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    ctxRef.current = ctx;
    notifyChange(false);
  }, [heightPx, minWidthPx, notifyChange]);

  useLayoutEffect(() => {
    initCanvas();
  }, [initCanvas]);

  const getPoint = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  }, []);

  const onPointerDown = useCallback(
    (e) => {
      if (e.button != null && e.button !== 0) return;
      e.preventDefault();
      const ctx = ctxRef.current;
      if (!ctx) return;
      drawingRef.current = true;
      const p = getPoint(e);
      lastRef.current = p;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      notifyChange(true);
    },
    [getPoint, notifyChange]
  );

  const onPointerMove = useCallback(
    (e) => {
      if (!drawingRef.current) return;
      e.preventDefault();
      const ctx = ctxRef.current;
      if (!ctx) return;
      const p = getPoint(e);
      const last = lastRef.current;
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      lastRef.current = p;
    },
    [getPoint]
  );

  const endStroke = useCallback(() => {
    drawingRef.current = false;
  }, []);

  const clear = useCallback(() => {
    initCanvas();
  }, [initCanvas]);

  useImperativeHandle(
    ref,
    () => ({
      clear,
      getCanvas: () => canvasRef.current,
      hasSignature: () => hasSignature,
      exportPdf: (opts) => canvasToSignaturePdf(canvasRef.current, opts)
    }),
    [clear, hasSignature]
  );

  return (
    <div className={className}>
      {label != null && <div className="form-label mb-1">{label}</div>}
      {hint != null && <p className="text-muted small mb-2 mb-md-3">{hint}</p>}
      <div ref={containerRef} className="border rounded bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          className="d-block w-100"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endStroke}
          onPointerLeave={endStroke}
          onPointerCancel={endStroke}
          role="img"
          aria-label={ariaLabel}
        />
      </div>
      <div className="d-flex justify-content-end mt-2">
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={clear}>
          {clearLabel}
        </button>
      </div>
      {showError && errorMessage != null && (
        <div className="invalid-feedback d-block mt-1">{errorMessage}</div>
      )}
    </div>
  );
});

export default SignaturePad;

SignaturePad.propTypes = {
  heightPx: PropTypes.number,
  minWidthPx: PropTypes.number,
  label: PropTypes.string,
  hint: PropTypes.string,
  clearLabel: PropTypes.string,
  showError: PropTypes.bool,
  errorMessage: PropTypes.string,
  onSignatureChange: PropTypes.func,
  className: PropTypes.string,
  ariaLabel: PropTypes.string
};
